import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Expand,
  Minimize,
  FileText,
  Download,
  Loader2,
} from "lucide-react";
import { useGetSlideGenerator } from "@packages/api/madlenAPI";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@packages/contexts/ToastContext";
import schemas from "../../components/schemas";
import { exportToPDF, exportToPPTX } from "../../components/export";
import { Progress } from "@core/progress";
import { Button } from "@core/button";

const SingleSlideView = () => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const [exportProgress, setExportProgress] = useState<{
    type: "pdf" | "pptx" | null;
    current: number;
    total: number;
    message: string;
  }>({ type: null, current: 0, total: 0, message: "" });
  const thumbnailRefs = useRef<HTMLDivElement[]>([]);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();
  const { t } = useTranslation();
  const { addToast } = useToast();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target as Node)
      ) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { data: slideGenerator } = useGetSlideGenerator(Number(id), {
    query: {
      enabled: id !== undefined,
    },
  });

  const slideComponents = useMemo(() => {
    if (!slideGenerator?.presentation) return [];

    const processedSlides = (slideGenerator?.presentation.slides as any[])
      .map((slideConfig, index) => {
        const schemaId = Object.keys(slideConfig)[0];
        const schema = schemas.find((s: any) => s.id === schemaId);

        if (schema) {
          const uniqueId = `${schema.id}_${Date.now()}_${index}`;
          const uniqueSchema = {
            ...schema,
            uniqueId,
            originalId: schema.id,
          };

          // setSelectedSchemas(prev => [...prev, uniqueSchema]);

          const config = slideConfig[schemaId];
          // setSlideContents(prev => ({
          //   ...prev,
          //   [uniqueId]: config
          // }));

          return {
            schema: uniqueSchema,
            content: config,
          };
        }
        return null;
      })
      .filter(Boolean);

    return processedSlides;
  }, [slideGenerator, currentSlideIndex]);

  // Navigation handlers
  const goToNextSlide = useCallback(() => {
    if (
      slideGenerator &&
      slideGenerator.presentation &&
      currentSlideIndex <
        (slideGenerator.presentation.slides as {}[]).length - 1
    ) {
      setCurrentSlideIndex((prev) => prev + 1);
    }
  }, [currentSlideIndex, slideGenerator]);

  const goToPreviousSlide = useCallback(() => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex((prev) => prev - 1);
    }
  }, [currentSlideIndex]);

  // Fullscreen handler
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const scrollToCurrentThumbnail = useCallback(
    (smooth = true, delay = 0) => {
      setTimeout(() => {
        if (
          thumbnailRefs.current &&
          thumbnailRefs.current[currentSlideIndex] &&
          thumbnailContainerRef.current
        ) {
          const thumbnail = thumbnailRefs.current[currentSlideIndex];
          const container = thumbnailContainerRef.current;

          // Calculate the center position
          const containerWidth = container.offsetWidth;
          const thumbnailWidth = thumbnail.offsetWidth;
          const totalWidth = thumbnailWidth * slideComponents.length;

          let scrollLeft =
            thumbnail.offsetLeft - containerWidth / 2 + thumbnailWidth / 2;

          // Handle edge cases
          if (currentSlideIndex === 0) {
            // If it's the first slide, align to start
            scrollLeft = 0;
          } else if (currentSlideIndex === slideComponents.length - 1) {
            // If it's the last slide, align to end
            scrollLeft = totalWidth - containerWidth;
          }

          container.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: smooth ? "smooth" : "auto",
          });
        }
      }, delay);
    },
    [currentSlideIndex, slideComponents.length],
  );

  useEffect(() => {
    if (!isFullscreen) {
      const timeoutId = setTimeout(scrollToCurrentThumbnail, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [currentSlideIndex, isFullscreen]);

  useEffect(() => {
    const handleKeyPress = (e: any) => {
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
          goToNextSlide();
          break;
        case "ArrowLeft":
        case "ArrowUp":
          goToPreviousSlide();
          break;
        case "f":
          toggleFullscreen();
          break;
        case "Escape":
          if (isFullscreen) {
            document.exitFullscreen();
            setIsFullscreen(false);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [goToNextSlide, goToPreviousSlide, isFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleExport = async (type: "pdf" | "pptx") => {
    if (isExporting || !slideComponents.length) return;

    setIsExporting(true);
    try {
      const exportFunction = type === "pdf" ? exportToPDF : exportToPPTX;
      await exportFunction(
        slideComponents,
        slideGenerator?.topic,
        (progress) => {
          setExportProgress({
            type,
            current: progress.current,
            total: progress.total,
            message: progress.message,
          });
          if (progress.error) {
            addToast({
              title: t("common.error"),
              description: progress.message,
              variant: "error",
            });
          } else if (progress.done) {
            addToast({
              title: t("common.success"),
              description: progress.message,
              variant: "success",
            });
          }
        },
      );
    } catch (error) {
      addToast({
        title: t("common.error"),
        description: t("slides.export.error.unexpected"),
        variant: "error",
      });
    } finally {
      setIsExporting(false);
      setTimeout(() => {
        setExportProgress({ type: null, current: 0, total: 0, message: "" });
      }, 2000);
    }
  };

  if (!slideGenerator || !slideGenerator.presentation) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="p-4 bg-violet-50 dark:bg-violet-900/30 rounded-full">
            <Loader2 className="w-8 h-8 text-violet-600 dark:text-violet-400 animate-spin" />
          </div>
          <div className="flex items-center space-x-2">
            <div
              className="w-2 h-2 bg-violet-500/60 dark:bg-violet-400/60 rounded-full animate-pulse"
              style={{ animationDelay: "0ms", animationDuration: "1s" }}
            />
            <div
              className="w-2 h-2 bg-violet-500/60 dark:bg-violet-400/60 rounded-full animate-pulse"
              style={{ animationDelay: "300ms", animationDuration: "1s" }}
            />
            <div
              className="w-2 h-2 bg-violet-500/60 dark:bg-violet-400/60 rounded-full animate-pulse"
              style={{ animationDelay: "600ms", animationDuration: "1s" }}
            />
          </div>
          <p className="text-gray-600 dark:text-gray-300">
            {t("common.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isFullscreen) {
    if (!slideComponents[currentSlideIndex]) return null;

    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="relative w-full h-full flex items-center justify-center">
          <div
            key={slideComponents[currentSlideIndex].schema.uniqueId}
            className="w-full h-full flex items-center justify-center p-4"
            style={{ maxWidth: "1920px" }}
          >
            {slideComponents[currentSlideIndex].schema.render(
              slideComponents[currentSlideIndex].content,
            )}
          </div>

          {/* Slide Number Indicator */}
          <div className="absolute bottom-4 right-4 text-white/70 font-medium text-lg opacity-0 hover:opacity-100 transition-opacity duration-300">
            {currentSlideIndex + 1} / {slideComponents.length}
          </div>
          {/* Minimal Navigation Controls */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4 opacity-0 hover:opacity-100 transition-opacity duration-300 z-[9999]">
            <Button
              onClick={goToPreviousSlide}
              rounded="full"
              size="icon"
              bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
              disabled={currentSlideIndex === 0}
              className={`p-2  ${
                currentSlideIndex === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </Button>
            <Button
              onClick={goToNextSlide}
              rounded="full"
              size="icon"
              bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white"
              disabled={currentSlideIndex === slideComponents.length - 1}
              className={`p-2  ${
                currentSlideIndex === slideComponents.length - 1
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <ArrowRight className="w-6 h-6 text-white" />
            </Button>
          </div>

          {/* Fullscreen Exit Button */}
          <Button
            bgColor="bg-black/50 hover:bg-black/70"
            rounded="full"
            onClick={toggleFullscreen}
            size="sm"
            className="absolute top-4 right-4 opacity-50 hover:opacity-100"
          >
            <Minimize className="w-6 h-6 text-white" />
          </Button>

          {/* Export button with dropdown */}
          <div className="absolute top-4 left-4 z-[9999]">
            <div className="relative" ref={exportDropdownRef}>
              <Button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                disabled={isExporting}
                rounded="lg"
                bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
                className="gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                size="sm"
              >
                <Download className="w-4 h-4" />
                <span>{t("slides.preview.export")}</span>
              </Button>
              {showExportDropdown && (
                <div className="absolute left-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <Button
                    onClick={() => {
                      handleExport("pdf");
                      setShowExportDropdown(false);
                    }}
                    fullWidth
                    variant="solid"
                    bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                    size="md"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{t("slides.preview.exportPDF")}</span>
                  </Button>
                  <Button
                    onClick={() => {
                      handleExport("pptx");
                      setShowExportDropdown(false);
                    }}
                    fullWidth
                    variant="solid"
                    bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                    size="md"
                  >
                    <Download className="w-4 h-4" />
                    <span>{t("slides.preview.exportPPTX")}</span>
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="w-full h-full flex items-center justify-center">
            <div
              className="relative w-full h-full flex items-center justify-center"
              style={{ maxWidth: "1920px" }}
            >
              <div className="aspect-[16/9] w-full max-h-full">
                {slideComponents[currentSlideIndex].schema.render(
                  slideComponents[currentSlideIndex].content,
                )}
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4 z-[9999]">
            <Button
              onClick={goToPreviousSlide}
              disabled={currentSlideIndex === 0}
              className={`w-10 h-10 shadow-lg z-[9999] ${
                currentSlideIndex === 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
              rounded="full"
              bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <Button
              onClick={goToNextSlide}
              rounded="full"
              bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
              disabled={currentSlideIndex === slideComponents.length - 1}
              className={`w-10 h-10 shadow-lg z-[9999] ${
                currentSlideIndex === slideComponents.length - 1
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <ArrowRight className="w-6 h-6" />
            </Button>
          </div>

          {/* Fullscreen Toggle */}
          <Button
            size="icon"
            onClick={toggleFullscreen}
            variant="solid"
            bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
            rounded="full"
            className="absolute top-4 right-4 p-2 shadow-lg z-[9999]"
            title={t("slides.preview.fullscreen")}
          >
            <Expand className="w-6 h-6" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-gray-100 to-rose-100 dark:from-gray-900 dark:to-rose-900 overflow-hidden">
      <div className="h-full container max-w-[1920px] mx-auto p-4">
        <div className="h-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4">
          <div className="h-full flex flex-col">
            {/* Main Preview - Updated to handle overflow properly */}
            <div className="flex-1 relative min-h-0 overflow-hidden">
              {slideComponents[currentSlideIndex] && (
                <div className="absolute inset-0 flex items-center justify-center z-[9999]">
                  {/* Export button with dropdown */}
                  <div className="absolute top-4 left-4 z-[9999]">
                    <div className="relative" ref={exportDropdownRef}>
                      <Button
                        onClick={() =>
                          setShowExportDropdown(!showExportDropdown)
                        }
                        disabled={isExporting}
                        rounded="lg"
                        size="md"
                        bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
                        className="shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      {showExportDropdown && (
                        <div className="absolute left-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                          <Button
                            onClick={() => {
                              handleExport("pdf");
                              setShowExportDropdown(false);
                            }}
                            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                            fullWidth
                            className="flex"
                            size="md"
                          >
                            <FileText className="w-4 h-4" />
                            <span>{t("slides.preview.exportPDF")}</span>
                          </Button>
                          <Button
                            onClick={() => {
                              handleExport("pptx");
                              setShowExportDropdown(false);
                            }}
                            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                            fullWidth
                            size="md"
                            className="flex"
                          >
                            <Download className="w-4 h-4" />
                            <span>{t("slides.preview.exportPPTX")}</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-full h-full flex items-center justify-center">
                    <div
                      className="relative w-full h-full flex items-center justify-center"
                      style={{ maxWidth: "1920px" }}
                    >
                      <div className="aspect-[16/9] w-full max-h-full">
                        {slideComponents[currentSlideIndex].schema.render(
                          slideComponents[currentSlideIndex].content,
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-4 z-[9999]">
                    <Button
                      onClick={goToPreviousSlide}
                      disabled={currentSlideIndex === 0}
                      size="icon"
                      rounded="full"
                      bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
                      className={`w-10 h-10 hadow-lg z-[9999] ${
                        currentSlideIndex === 0
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <ArrowLeft className="w-6 h-6" />
                    </Button>
                    <Button
                      onClick={goToNextSlide}
                      disabled={
                        currentSlideIndex === slideComponents.length - 1
                      }
                      size="icon"
                      rounded="full"
                      bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
                      className={`w-10 h-10 shadow-lg z-[9999] ${
                        currentSlideIndex === slideComponents.length - 1
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <ArrowRight className="w-6 h-6" />
                    </Button>
                  </div>

                  {/* Fullscreen Toggle */}
                  <Button
                    onClick={toggleFullscreen}
                    bgColor="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700"
                    size="icon"
                    rounded="full"
                    className="absolute top-4 right-4 shadow-lg z-[9999]"
                    title={t("slides.preview.fullscreen")}
                  >
                    <Expand className="w-6 h-6" />
                  </Button>
                </div>
              )}
            </div>

            {/* Thumbnails - Using responsive height instead of fixed height */}
            <div className="min-h-[120px] max-h-[140px] mt-4 w-full">
              <div
                ref={thumbnailContainerRef}
                className="flex gap-2 h-full overflow-x-auto px-2 scroll-smooth"
                style={{
                  scrollbarWidth: "thin",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <div style={{ minWidth: "calc(50% - 100px)" }} />

                {slideComponents.map((slide: any, index: any) => (
                  <div
                    key={index}
                    ref={(el) => {
                      if (el) {
                        thumbnailRefs.current[index] = el;
                      }
                    }}
                    onClick={() => {
                      setCurrentSlideIndex(index);
                      scrollToCurrentThumbnail(true, 0);
                    }}
                    className={`cursor-pointer rounded-lg flex-shrink-0 h-full aspect-[16/9] 
                      transition-all duration-300 transform
                      ${
                        currentSlideIndex === index
                          ? "scale-105 bg-rose-100 dark:bg-rose-900 border-2 border-rose-500 shadow-lg"
                          : "bg-gray-50 dark:bg-gray-700 border-2 border-transparent hover:scale-105"
                      }`}
                  >
                    <div className="h-full relative">
                      <div className="w-full h-full rounded-lg overflow-hidden">
                        <div
                          className="absolute inset-0"
                          style={{
                            transform: "scale(0.25)",
                            transformOrigin: "top left",
                          }}
                        >
                          <div style={{ width: "400%", height: "400%" }}>
                            {slide.schema.render(slide.content)}
                          </div>
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white px-2 py-1 text-xs">
                        {t("slides.preview.slideCount", {
                          current: index + 1,
                          total: slideComponents.length,
                        })}
                      </div>
                    </div>
                  </div>
                ))}

                <div style={{ minWidth: "calc(50% - 100px)" }} />
              </div>
            </div>
          </div>

          {/* Export Progress */}
          {isExporting && exportProgress.type && (
            <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg p-4 transition-all">
              <div className="container max-w-7xl mx-auto">
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t(
                        exportProgress.type === "pdf"
                          ? "slides.export.title.pdf"
                          : "slides.export.title.pptx",
                      )}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t("slides.preview.slideCount", {
                        current: exportProgress.current,
                        total: exportProgress.total,
                      })}
                    </span>
                  </div>
                  <Progress
                    value={
                      (exportProgress.current / exportProgress.total) * 100
                    }
                    className="h-2 bg-gray-200 dark:bg-gray-700"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {exportProgress.message}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleSlideView;
